// Light channel model — RGB (24), WWH (25) and WWRGB (23).
//
// Every light speaks plain comma-separated channel values, 0–255, sized to the
// channel count. There is NO 'cs' prefix and no command word:
//
//   RGB    "<r>,<g>,<b>"                    e.g. 255,0,0
//   WWH    "<white>,<warmWhite>"            e.g. 255,0
//   WWRGB  "<white>,<warm>,<r>,<g>,<b>"     e.g. 0,0,255,0,0
//
// The status echoed on `<uno>/client` is the same shape, so a screen can seed
// itself from the status string with the same parser it uses for live updates.
//
// Preset payloads below are copied verbatim from the Flutter buttons — note
// WWRGB "Red" is 0,0,255,0,0 (white and warm lead), which is easy to get wrong.

export const LIGHT_CHANNELS = { rgb: 3, wwh: 2, wwrgb: 5 };

export function channelCount(typeKey) {
  return LIGHT_CHANNELS[typeKey] || 3;
}

// "12,0,255" -> [12, 0, 255], padded/truncated to `count`. Returns null when the
// string isn't a usable channel list, so callers can leave the state unknown
// rather than forcing every channel to 0.
export function parseChannels(raw, count) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const parts = s.split(',').map((x) => Number.parseInt(x.trim(), 10));
  if (!parts.length || parts.some((n) => Number.isNaN(n))) return null;
  return Array.from({ length: count }, (_, i) => {
    const v = parts[i];
    return Number.isFinite(v) ? Math.min(255, Math.max(0, v)) : 0;
  });
}

export function formatChannels(values) {
  return values.map((v) => Math.round(v)).join(',');
}

// Flutter's preset buttons, in the order they appear on each screen.
export const RGB_PRESETS = [
  { label: 'Red', command: '255,0,0', swatch: '#D32F2F' },
  { label: 'Blue', command: '0,0,255', swatch: '#0000C8' },
  { label: 'Green', command: '0,255,0', swatch: '#39FF00' },
  { label: 'Off', command: '0,0,0', swatch: '#000000' },
];

export const WWH_PRESETS = [
  { label: 'White', command: '255,0', swatch: '#FFFFFF' },
  { label: 'Warm\nWhite', command: '0,255', swatch: '#FFD08A' },
  { label: ' Cool\nWhite', command: '128,128', swatch: '#E8F1FF' },
  { label: 'Off', command: '0,0', swatch: '#000000' },
];

// Channel order is white, warmWhite, R, G, B — which is why "Red" is
// 0,0,255,0,0 and not 255,0,0,0,0. Easy to get wrong; these are verified against
// the Dart, EXCEPT "Warm White", which the Flutter screen doesn't offer as a
// preset. It is derived from the channel layout (channel 2) and matches the WWH
// screen's warm preset, so it follows the same convention as the rest.
export const WWRGB_PRESETS = [
  { label: 'White', command: '255,0,0,0,0', swatch: '#FFFFFF' },
  { label: 'Warm\nWhite', command: '0,255,0,0,0', swatch: '#FFD08A' },
  { label: 'Red', command: '0,0,255,0,0', swatch: '#D32F2F' },
  { label: 'Green', command: '0,0,0,255,0', swatch: '#39FF00' },
  { label: 'Blue', command: '0,0,0,0,255', swatch: '#0000C8' },
  { label: 'Off', command: '0,0,0,0,0', swatch: '#000000' },
];

// ── HSV <-> RGB, for the colour wheel ──────────────────────────────────────
export function hsvToRgb(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

export function rgbToHsv(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = 60 * (((gn - bn) / d) % 6);
    else if (max === gn) h = 60 * ((bn - rn) / d + 2);
    else h = 60 * ((rn - gn) / d + 4);
  }
  if (h < 0) h += 360;
  return [h, max === 0 ? 0 : d / max, max];
}

export function rgbCss(r, g, b) {
  return `rgb(${r}, ${g}, ${b})`;
}
